import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Plus, Send, Merge, Copy, Trash2, Timer, Play, X } from "lucide-react";
import { DateBar } from "@/v2/components/DateBar";
import { EntryCard } from "@/v2/components/EntryCard";
import { SelectionBar } from "@/v2/components/SelectionBar";
import { Fab } from "@/v2/components/ui/fab";
import { cn } from "@/v2/lib/utils";
import { useDateParams } from "@/v2/hooks/useDateParams";
import { useRubberBand } from "@/v2/hooks/useRubberBand";
import { useData } from "@/v2/contexts/DataProvider";
import { useActions } from "@/v2/contexts/ActionQueueProvider";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
} from "@/v2/components/ui/responsive-dialog";
import { EntryForm, type EntryFormHandle } from "@/v2/components/entry-form/EntryForm";
import { useTimerContext } from "@/v2/contexts/TimerProvider";
import { Button } from "@/v2/components/ui/button";
import { Switch } from "@/v2/components/ui/switch";
import { Label } from "@/v2/components/ui/label";
import { CombineDialog } from "@/v2/components/CombineDialog";
import { Banner } from "@/v2/components/ui/banner";
import { FilterPopover, applyFilters, classifyEntry, DEFAULT_FILTER, type FilterState } from "@/v2/components/FilterPopover";
import { toast } from "sonner";
import { SortPopover, sortAndGroup, DEFAULT_SORT, DEFAULT_GROUP, type SortMode, type GroupMode } from "@/v2/components/SortPopover";
import { SwipeableCard } from "@/v2/components/SwipeableCard";
import { parseEntryDate } from "@/v2/lib/dates";
import { useMattersCache, useRestrictedWords } from "@/shared/cache";
import type { Entry } from "@/shared/types/entries";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";

function formatDateForApi(d: Date): string {
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	const year = d.getFullYear();
	return `${month}/${day}/${year}`;
}

function formatTimerDisplay(totalSeconds: number): string {
	const h = Math.floor(totalSeconds / 3600);
	const m = Math.floor((totalSeconds % 3600) / 60);
	const s = totalSeconds % 60;
	const mm = String(m).padStart(2, "0");
	const ss = String(s).padStart(2, "0");
	return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Convert seconds to billing units (1 unit = 6 min = 0.1h), floor at 0.1 */
function secondsToUnits(seconds: number): number {
	return Math.max(0.1, Math.floor(seconds / 360) / 10);
}

export default function Tracked() {
	useDocumentTitle("Tracked");
	const { date, dateRange, setDate, setDateRange } = useDateParams();
	const { entries, isLoading } = useData();
	const actions = useActions();
	const timer = useTimerContext();
	const { matters } = useMattersCache();
	const { getRestrictedWords } = useRestrictedWords();
	const [addOpen, setAddOpen] = useState(false);
	const [editEntry, setEditEntry] = useState<Entry | null>(null);
	const [combineEntries, setCombineEntries] = useState<Entry[] | null>(null);
	const [prefillDuration, setPrefillDuration] = useState<number | null>(null);
	const [createMore, setCreateMore] = useState(() => {
		try { return localStorage.getItem("v2:create-more") === "true"; } catch { return false; }
	});
	const [createMoreKey, setCreateMoreKey] = useState(0);
	const [carryForward, setCarryForward] = useState<Partial<TimeEntryFormValues> | null>(null);
	const addFormRef = useRef<EntryFormHandle>(null);
	const editFormRef = useRef<EntryFormHandle>(null);

	// Filter & sort state
	const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
	const [sort, setSort] = useState<SortMode>(DEFAULT_SORT);
	const [group, setGroup] = useState<GroupMode>(DEFAULT_GROUP);

	// When timer pill is tapped in stopped state, open dialog with duration prefilled
	useEffect(() => {
		if (timer.pendingDuration !== null) {
			setPrefillDuration(timer.pendingDuration);
			timer.clearPendingDuration();
			setAddOpen(true);
		}
	}, [timer.pendingDuration]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleStartTimer = useCallback(() => {
		timer.start();
		setAddOpen(false);
	}, [timer]);

	// Ref to find the shell container (parent of main) for portaling the selection bar over the header
	const pageRef = useRef<HTMLDivElement>(null);
	const hasInitiallyScrolled = useRef(false);
	const [shellContainer, setShellContainer] = useState<HTMLElement | null>(null);
	useEffect(() => {
		// The shell container is the flex parent that contains header + main + bottom nav
		const main = pageRef.current?.closest("main");
		if (main?.parentElement) setShellContainer(main.parentElement);
	}, []);

	// Bouncy overscroll
	useRubberBand(pageRef);

	// Selection state
	const [selectionMode, setSelectionMode] = useState(false);
	const [checkedRows, setCheckedRows] = useState<Set<number>>(new Set());
	const lastClickedId = useRef<number | null>(null);

	const enterSelectionMode = useCallback((entry: Entry) => {
		setSelectionMode(true);
		setCheckedRows(new Set([entry.id]));
		lastClickedId.current = entry.id;
	}, []);

	const exitSelectionMode = useCallback(() => {
		setSelectionMode(false);
		setCheckedRows(new Set());
		lastClickedId.current = null;
	}, []);

	// Filter entries by selected date or range
	const dateFilteredEntries = useMemo(() => {
		return entries.filter((e) => {
			const d = parseEntryDate(e.date);
			if (dateRange.from && dateRange.to) {
				const start = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
				const end = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate(), 23, 59, 59, 999);
				return d >= start && d <= end;
			}
			return (
				d.getFullYear() === date.getFullYear() &&
				d.getMonth() === date.getMonth() &&
				d.getDate() === date.getDate()
			);
		});
	}, [entries, date, dateRange]);

	// Apply user filters (app, status)
	const filteredEntries = useMemo(
		() => applyFilters(dateFilteredEntries, filter, matters, getRestrictedWords),
		[dateFilteredEntries, filter, matters, getRestrictedWords],
	);

	// Sort + group
	const isMultiDate = !!(dateRange.from && dateRange.to);
	const groupedEntries = useMemo(
		() => sortAndGroup(filteredEntries, sort, group, isMultiDate),
		[filteredEntries, sort, group, isMultiDate],
	);

	const groupKeys = Object.keys(groupedEntries);

	// Flat ordered IDs matching visual display order (for shift+click range)
	const flatEntryIds = useMemo(() => {
		const ids: number[] = [];
		for (const key of groupKeys) {
			for (const entry of groupedEntries[key]) ids.push(entry.id);
		}
		return ids;
	}, [groupedEntries, groupKeys]);

	// Excel-style selection handler (must be after flatEntryIds)
	const handleSelect = useCallback((entry: Entry, e: React.MouseEvent) => {
		const hasCtrl = e.ctrlKey || e.metaKey;
		const hasShift = e.shiftKey;

		if (hasShift && lastClickedId.current !== null) {
			const from = flatEntryIds.indexOf(lastClickedId.current);
			const to = flatEntryIds.indexOf(entry.id);
			if (from !== -1 && to !== -1) {
				const start = Math.min(from, to);
				const end = Math.max(from, to);
				const rangeIds = flatEntryIds.slice(start, end + 1);
				if (hasCtrl) {
					const next = new Set(checkedRows);
					for (const rid of rangeIds) next.add(rid);
					setCheckedRows(next);
				} else {
					setCheckedRows(new Set(rangeIds));
				}
				return;
			}
		}

		if (hasCtrl) {
			// toggle without clearing
			setCheckedRows((prev) => {
				const next = new Set(prev);
				if (next.has(entry.id)) next.delete(entry.id);
				else next.add(entry.id);
				return next;
			});
		} else {
			// plain click — select only this entry
			setCheckedRows(new Set([entry.id]));
		}
		lastClickedId.current = entry.id;
	}, [flatEntryIds, checkedRows]);

	const handleIconClick = useCallback((entry: Entry, e: React.MouseEvent) => {
		if (selectionMode) {
			handleSelect(entry, e);
		} else {
			enterSelectionMode(entry);
		}
	}, [selectionMode, handleSelect, enterSelectionMode]);

	const handleRowSelect = useCallback((entry: Entry, e: React.MouseEvent) => {
		handleSelect(entry, e);
	}, [handleSelect]);

	// Auto-scroll: to bottom for today, to top for other dates on first load
	const isToday = date.toDateString() === new Date().toDateString();
	const autoScrollToBottom = !dateRange?.from && isToday;
	useEffect(() => {
		if (!pageRef.current || filteredEntries.length === 0) return;
		if (autoScrollToBottom) {
			pageRef.current.scrollTop = pageRef.current.scrollHeight;
		} else if (!hasInitiallyScrolled.current) {
			pageRef.current.scrollTop = 0;
			hasInitiallyScrolled.current = true;
		}
	}, [autoScrollToBottom, filteredEntries.length]);

	// Reset scroll tracking when date changes
	useEffect(() => {
		hasInitiallyScrolled.current = false;
	}, [date, dateRange]);

	// Clear selection when filter, date, or date range changes to prevent scope mismatch
	useEffect(() => {
		if (selectionMode) exitSelectionMode();
	}, [filter, date, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

	// Check if all entries on the current page are selected
	const currentPageIds = useMemo(() => new Set(filteredEntries.map((e) => e.id)), [filteredEntries]);
	const allCurrentPageSelected = filteredEntries.length > 0 && filteredEntries.every((e) => checkedRows.has(e.id));

	const selectAll = useCallback(() => {
		setCheckedRows((prev) => {
			const next = new Set(prev);
			if (allCurrentPageSelected) {
				// Deselect only current page entries, keep other dates
				for (const id of currentPageIds) next.delete(id);
			} else {
				// Add all current page entries, keep other dates
				for (const id of currentPageIds) next.add(id);
			}
			return next;
		});
	}, [allCurrentPageSelected, currentPageIds]);

	const handleCardClick = (entry: Entry) => {
		setEditEntry(entry);
	};

	const getSelectedIds = useCallback(
		() => filteredEntries.filter((e) => checkedRows.has(e.id)).map((e) => e.id),
		[filteredEntries, checkedRows],
	);

	const handleExport = useCallback(() => {
		const ids = getSelectedIds();
		if (ids.length === 0) return;
		actions.exportEntries(ids);
		exitSelectionMode();
	}, [getSelectedIds, actions, exitSelectionMode]);

	const handleCombine = useCallback(() => {
		const ids = getSelectedIds();
		if (ids.length < 2) return;
		const selected = filteredEntries.filter((e) => ids.includes(e.id));
		setCombineEntries(selected);
	}, [getSelectedIds, filteredEntries]);

	const handleDuplicate = useCallback(() => {
		const ids = getSelectedIds();
		if (ids.length === 0) return;
		actions.duplicateEntries(ids);
		exitSelectionMode();
	}, [getSelectedIds, actions, exitSelectionMode]);

	const handleDelete = useCallback(() => {
		const ids = getSelectedIds();
		if (ids.length === 0) return;
		actions.deleteEntries(ids);
		exitSelectionMode();
	}, [getSelectedIds, actions, exitSelectionMode]);

	return (
		<div ref={pageRef} className="relative flex flex-col min-h-full">
			{/* Selection bar — portaled over the header */}
			{selectionMode && shellContainer && createPortal(
				<div className="absolute inset-x-0 top-0 z-50 h-14 flex items-center bg-background border-b border-border">
					<SelectionBar
						selectedCount={checkedRows.size}
						allCurrentPageSelected={allCurrentPageSelected}
						someCurrentPageSelected={filteredEntries.some((e) => checkedRows.has(e.id)) && !allCurrentPageSelected}
						onSelectAll={selectAll}
						onClose={exitSelectionMode}
					/>
				</div>,
				shellContainer,
			)}

			{/* Date bar */}
			<div className="sticky top-0 z-20 bg-background border-b border-border">
				<DateBar
					date={date}
					dateRange={dateRange}
					onDateChange={setDate}
					onDateRangeChange={setDateRange}
					leftSlot={
						<FilterPopover
							filter={filter}
							onChange={setFilter}
							entries={dateFilteredEntries}
							matters={matters}
							getRestrictedWords={getRestrictedWords}
						/>
					}
					rightSlot={
						<SortPopover
							sort={sort}
							group={group}
							onSortChange={setSort}
							onGroupChange={setGroup}
						/>
					}
				/>
			</div>

			{/* Entry list */}
			<div className="flex-1">
				{isLoading ? (
					<>
						{[
							{ cards: [
								{ matter: "w-40", client: "w-28", lines: 3, codes: true },
								{ matter: "w-32", client: "w-36", lines: 2, codes: true },
								{ matter: "w-24", client: "w-20", lines: 2, codes: false },
							]},
							{ cards: [
								{ matter: "w-36", client: "w-24", lines: 3, codes: true },
								{ matter: "w-28", client: "w-32", lines: 2, codes: false },
							]},
						].map((group, g) => (
							<div key={g}>
								<div className="px-4 py-1.5 border-b border-border">
									<div className="h-3 w-16 rounded bg-muted animate-pulse" />
								</div>
								{group.cards.map((card, i) => (
									<div key={i} className="flex items-start gap-3 px-4 py-3 border-b border-border">
										<div className="size-8 shrink-0 rounded bg-muted animate-pulse" />
										<div className="flex-1 min-w-0">
											<div className="flex items-baseline justify-between gap-2">
												<div className={cn("h-3.5 rounded bg-muted animate-pulse", card.matter)} />
												<div className="h-3 w-14 rounded bg-muted animate-pulse shrink-0" />
											</div>
											<div className="flex items-center justify-between gap-2 mt-1.5">
												<div className={cn("h-3 rounded bg-muted animate-pulse", card.client)} />
												<div className="h-3 w-10 rounded bg-muted animate-pulse shrink-0" />
											</div>
											<div className="space-y-1.5 mt-2">
												<div className="h-3 w-full rounded bg-muted animate-pulse" />
												<div className="h-3 w-full rounded bg-muted animate-pulse" />
												{card.lines >= 3 && <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />}
											</div>
											{card.codes && (
												<div className="flex items-center gap-1.5 mt-2">
													<div className="h-5 w-10 rounded-full bg-muted animate-pulse" />
													<div className="h-5 w-10 rounded-full bg-muted animate-pulse" />
												</div>
											)}
										</div>
									</div>
								))}
							</div>
						))}
					</>
				) : filteredEntries.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-3">
						<p className="text-sm">
							{dateFilteredEntries.length > 0 && filteredEntries.length === 0
								? "No entries match the selected filters."
								: dateRange?.from && dateRange?.to && entries.length > 0
									? "No entries for this date range."
									: entries.length > 0
										? "No entries for this date."
										: "No entries yet. Tap + to add your first entry."}
						</p>
						{dateFilteredEntries.length > 0 && filteredEntries.length === 0 && (
							<button
								onClick={() => setFilter(DEFAULT_FILTER)}
								className="text-sm text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
							>
								Reset filters
							</button>
						)}
					</div>
				) : (
					<div>
						{groupKeys.map((groupKey) => (
							<div key={groupKey || "__flat"}>
								{groupKey && (
									<div className="sticky top-0 z-10 bg-secondary/80 backdrop-blur-sm px-4 py-1.5 text-xs font-medium text-muted-foreground border-b border-border flex items-center justify-between">
										<span>{groupKey}</span>
										<span>{groupedEntries[groupKey].reduce((sum, e) => sum + e.duration, 0).toFixed(1)} hrs</span>
									</div>
								)}
								{groupedEntries[groupKey].map((entry) => (
									<SwipeableCard
										key={`${entry.id}-${actions.failedExports.has(entry.id) ? 1 : 0}`}
										canSwipeRight={() => {
											const status = classifyEntry(entry, matters, getRestrictedWords);
											if (status === "incomplete") {
												toast.error("Entry is incomplete — fill in all required fields before exporting.");
												return false;
											}
											return true;
										}}
										onSwipeRight={() => {
											actions.exportEntries([entry.id]);
										}}
										onSwipeLeft={() => {
											actions.deleteEntries([entry.id]);
										}}
										disabled={selectionMode || actions.pendingEntryIds.has(entry.id)}
									>
										<EntryCard
											entry={entry}
											onClick={handleCardClick}
											selectionMode={selectionMode}
											selected={checkedRows.has(entry.id)}
											onIconClick={handleIconClick}
											onRowSelect={handleRowSelect}
											onLongPress={enterSelectionMode}
											exportFailed={actions.failedExports.has(entry.id)}
											isMutating={actions.pendingEntryIds.has(entry.id)}
										/>
									</SwipeableCard>
								))}
							</div>
						))}
					</div>
				)}
			</div>


			{/* Add entry dialog */}
			<ResponsiveDialog open={addOpen} onOpenChange={(open) => {
				setAddOpen(open);
				if (!open) {
					setPrefillDuration(null);
					setCarryForward(null);
					setCreateMoreKey(0);
				}
			}}>
				<ResponsiveDialogContent
					title="Add Entry"
					headerActions={
						prefillDuration !== null ? (
							<div className="flex h-9 rounded-md border border-input overflow-hidden">
								<button
									type="button"
									className="flex items-center gap-2 px-3 text-sm font-medium hover:bg-accent transition-colors"
									onClick={() => {
										timer.resume();
										setAddOpen(false);
										setPrefillDuration(null);
									}}
								>
									<Play className="size-3.5 fill-current" />
									<span className="tabular-nums">{formatTimerDisplay(prefillDuration)}</span>
								</button>
								<button
									type="button"
									aria-label="Discard timer"
									className="flex items-center justify-center w-9 border-l border-input text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
									onClick={() => {
										timer.dismiss();
										setPrefillDuration(null);
									}}
								>
									<X className="size-3.5" />
								</button>
							</div>
						) : timer.phase === "idle" ? (
							<Button variant="outline" size="sm" className="gap-2" onClick={handleStartTimer}>
								<Timer className="size-4" />
								Start Timer
							</Button>
						) : undefined
					}
					footer={
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-2">
								<Switch
									id="create-more"
									checked={createMore}
									onCheckedChange={(checked) => {
										const val = !!checked;
										setCreateMore(val);
										try { localStorage.setItem("v2:create-more", String(val)); } catch {}
									}}
								/>
								<Label htmlFor="create-more" className="text-sm text-muted-foreground cursor-pointer">
									Create more
								</Label>
							</div>
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => addFormRef.current?.submitExport()}
								>
									Export
								</Button>
								<Button type="submit" form="add-entry-form" size="sm">Add</Button>
							</div>
						</div>
					}
				>
					<EntryForm
						ref={addFormRef}
						key={createMoreKey}
						formId="add-entry-form"
						defaultValues={{
							date,
							...(prefillDuration !== null ? { duration: secondsToUnits(prefillDuration) } : {}),
							...carryForward,
						}}
						onSubmit={(values: TimeEntryFormValues) => {
							const dateStr = formatDateForApi(values.date);
							if (prefillDuration !== null) timer.dismiss();
							actions.addEntry(
								{
									id: 0,
									date: dateStr,
									matter: values.matter,
									matter_id: values.matter_id,
									description: values.description,
									duration: values.duration,
									task: values.task,
									activity: values.activity ?? "",
									end_time: new Date().toLocaleTimeString(),
								},
								createMore
									? () => {
											setCarryForward({
												date: values.date,
												matter_id: values.matter_id,
												matter: values.matter,
												task: values.task,
												activity: values.activity,
												duration: values.duration,
												description: values.description,
											});
											setPrefillDuration(null);
											setCreateMoreKey((k) => k + 1);
										}
									: () => setAddOpen(false),
							);
						}}
						onExport={(values: TimeEntryFormValues) => {
							const dateStr = formatDateForApi(values.date);
							if (prefillDuration !== null) timer.dismiss();
							actions.addEntry(
								{
									id: 0,
									date: dateStr,
									matter: values.matter,
									matter_id: values.matter_id,
									description: values.description,
									duration: values.duration,
									task: values.task,
									activity: values.activity ?? "",
									end_time: new Date().toLocaleTimeString(),
									shouldExport: true,
								},
								() => setAddOpen(false),
							);
						}}
					/>
				</ResponsiveDialogContent>
			</ResponsiveDialog>

			{/* Edit entry dialog */}
			<ResponsiveDialog open={editEntry !== null} onOpenChange={(open) => { if (!open) setEditEntry(null); }}>
				<ResponsiveDialogContent
					title="Edit Entry"
					onOpenAutoFocus={(e) => e.preventDefault()}
					footer={
						<div className="flex justify-end items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => editFormRef.current?.submitExport()}
							>
								Export
							</Button>
							<Button type="submit" form="edit-entry-form" size="sm">Save</Button>
						</div>
					}
				>
					{editEntry && (
						<EntryForm
							ref={editFormRef}
							formId="edit-entry-form"
							key={editEntry.id}
							defaultValues={{
								date: new Date(editEntry.date),
								matter_id: editEntry.matter_id ?? "",
								matter: editEntry.matter ?? "",
								task: editEntry.task ?? "",
								activity: editEntry.activity ?? "",
								duration: editEntry.duration ?? 0,
								description: editEntry.description ?? "",
							}}
							documentName={editEntry.document_name}
							banners={
								<>
									{actions.failedExports.has(editEntry.id) && (
										<Banner variant="error" title="Export Failed">
											{actions.failedExports.get(editEntry.id)}
										</Banner>
									)}
									{editEntry.attorney_feedback && (
										<Banner
											variant="warning"
											title="Admin Feedback"
											action={{
												label: "Resolve",
												onClick: () => {
													actions.resolveFeedback(editEntry.id);
													setEditEntry(null);
												},
											}}
										>
											{editEntry.attorney_feedback}
										</Banner>
									)}
								</>
							}
							onSubmit={(values: TimeEntryFormValues) => {
								const dateStr = formatDateForApi(values.date);
								actions.editEntry({
									id: editEntry!.id,
									date: dateStr,
									matter: values.matter,
									matter_id: values.matter_id,
									description: values.description,
									duration: values.duration,
									task: values.task,
									activity: values.activity ?? "",
								});
								setEditEntry(null);
							}}
							onExport={(values: TimeEntryFormValues) => {
								const dateStr = formatDateForApi(values.date);
								actions.editThenExport({
									id: editEntry!.id,
									date: dateStr,
									matter: values.matter,
									matter_id: values.matter_id,
									description: values.description,
									duration: values.duration,
									task: values.task,
									activity: values.activity ?? "",
								});
								setEditEntry(null);
							}}
							validateOnMount
						/>
					)}
				</ResponsiveDialogContent>
			</ResponsiveDialog>

			{/* Combine dialog */}
			<CombineDialog
				entries={combineEntries ?? []}
				open={combineEntries !== null}
				onOpenChange={(open) => { if (!open) setCombineEntries(null); }}
				onConfirm={(ids, matterId, mainEntryId) => {
					actions.combineEntries(ids, matterId, mainEntryId);
					setCombineEntries(null);
					exitSelectionMode();
				}}
			/>

			{/* Floating action buttons */}
			<div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom)+12px)] right-4 z-30 flex flex-col items-end gap-2">
				{/* Action button group — slides in from right */}
				<div
					className={cn(
						"flex flex-col rounded-2xl overflow-hidden shadow-lg border border-border transition-all duration-200 ease-out origin-bottom-right",
						selectionMode
							? "scale-100 opacity-100"
							: "scale-0 opacity-0 pointer-events-none",
					)}
				>
					{[
						{ icon: Merge, label: "Combine", onClick: handleCombine },
						{ icon: Copy, label: "Duplicate", onClick: handleDuplicate },
						{ icon: Trash2, label: "Delete", onClick: handleDelete },
					].map(({ icon: Icon, label, onClick }, i) => (
						<button
							key={label}
							onClick={onClick}
							className={cn(
								"h-11 w-11 inline-flex items-center justify-center bg-secondary text-secondary-foreground transition-colors active:bg-secondary/80 [&_svg]:size-[18px]",
								i > 0 && "border-t border-border",
							)}
						>
							<Icon />
						</button>
					))}
				</div>

				{/* Morphing FAB — single button that transitions between add/export */}
				<Fab
					className={cn(
						"transition-all duration-200",
						selectionMode ? "bg-primary" : "bg-highlight",
					)}
					onClick={selectionMode ? handleExport : () => setAddOpen(true)}
				>
					<span className={cn(
						"absolute transition-all duration-200",
						selectionMode ? "opacity-0 rotate-90 scale-0" : "opacity-100 rotate-0 scale-100",
					)}>
						<Plus className="size-6" />
					</span>
					<span className={cn(
						"absolute transition-all duration-200",
						selectionMode ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-0",
					)}>
						<Send className="size-6" />
					</span>
				</Fab>
			</div>
		</div>
	);
}
